import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import AddCustomerModal from '../../components/ui/AddCustomerModal';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import { listCustomers, createCustomer as apiCreateCustomer, deleteCustomer as apiDeleteCustomer } from '../../services/customers';

const Customers = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await listCustomers({ search: searchQuery, page, limit });
      const rows = (res?.data || []).map((c) => ({
        id: c._id,
        firstName: c.firstName,
        lastName: c.lastName,
        email: c.email,
        phone: c.phone || '',
        licence: '-',
        role: c.organization || '-',
        status: c.status || 'Active',
        avatar: (c.firstName || 'C').charAt(0).toUpperCase()
      }));
      setCustomers(rows);
      setTotal(res?.pagination?.total || rows.length);
    } catch (e) {
      console.error('Failed to load customers', e);
      setCustomers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, [searchQuery, page]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleAddCustomer = async (formData) => {
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        organization: formData.organization || undefined,
        purposeOfUse: formData.purposeOfUse || undefined,
        status: formData.status || 'Active',
        note: formData.note || undefined
      };
      // Remove undefined values
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      await apiCreateCustomer(payload);
      setIsAddModalOpen(false);
      fetchCustomers();
    } catch (e) {
      console.error('Create customer failed', e);
      alert(e?.data?.error || e?.message || 'Failed to create customer');
    }
  };

  // Define table columns
  const columns = [
    {
      header: 'First Name',
      accessor: 'firstName',
      className: 'text-gray-900'
    },
    {
      header: 'Last Name',
      accessor: 'lastName',
      className: 'text-gray-900'
    },
    {
      header: 'Email',
      accessor: 'email',
      className: 'text-gray-600'
    },
    {
      header: 'Phone',
      accessor: 'phone',
      className: 'text-gray-600'
    },
    {
      header: 'Licence',
      accessor: 'licence',
      className: 'text-gray-900'
    },
    {
      header: 'Role',
      accessor: 'role',
      className: 'text-gray-600'
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (row) => {
        const statusConfig = {
          'Active': {
            bg: 'bg-blue-50',
            text: 'text-blue-600',
            border: 'border-blue-200',
            dot: 'bg-blue-500'
          },
          'Quotation Sent': {
            bg: 'bg-purple-50',
            text: 'text-purple-600',
            border: 'border-purple-200',
            dot: 'bg-purple-500'
          },
          'Suspended': {
            bg: 'bg-orange-50',
            text: 'text-orange-600',
            border: 'border-orange-200',
            dot: 'bg-orange-500'
          }
        };

        const config = statusConfig[row.status] || statusConfig['Active'];

        return (
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
            {row.status}
          </span>
        );
      }
    }
  ];

  // Action menu items
  const actionMenuItems = [
    { 
      label: 'View Details', 
      action: 'view',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      label: 'Edit Customer', 
      action: 'edit',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M11.3333 2.00004C11.5084 1.82494 11.716 1.68605 11.9439 1.59129C12.1717 1.49653 12.4154 1.44775 12.6616 1.44775C12.9079 1.44775 13.1516 1.49653 13.3794 1.59129C13.6072 1.68605 13.8149 1.82494 13.99 2.00004C14.1651 2.17513 14.304 2.38283 14.3987 2.61065C14.4935 2.83847 14.5423 3.08213 14.5423 3.32837C14.5423 3.57461 14.4935 3.81827 14.3987 4.04609C14.304 4.27391 14.1651 4.48161 13.99 4.65671L5.00001 13.6467L1.33334 14.6667L2.35334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      label: 'Delete', 
      action: 'delete',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33334 13.687 3.33334 13.3333V4M5.33334 4V2.66667C5.33334 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33334C9.68696 1.33333 10.0261 1.47381 10.2762 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      danger: true 
    }
  ];

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;
    const id = customerToDelete.id?.toString?.() ?? customerToDelete.id;
    if (!id) {
      alert('Invalid customer');
      return;
    }
    try {
      await apiDeleteCustomer(id);
      fetchCustomers();
    } catch (e) {
      console.error('Delete failed', e);
      alert(e?.data?.error || e?.message || 'Failed to delete');
      throw e;
    }
  };

  const handleAction = (action, row) => {
    if (action === 'view') {
      navigate(`/customer-details/${row.id}`);
    } else if (action === 'edit') {
      navigate(`/customer-details/${row.id}`);
    } else if (action === 'delete') {
      // DataTable passes the full row as second argument (not an id string)
      setCustomerToDelete(row);
      setIsDeleteModalOpen(true);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Customer Database</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search - minimal theme */}
          <div className="relative flex-1 sm:w-72">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73827c]" aria-hidden>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z" />
                <path d="M19 19l-4.35-4.35" />
              </svg>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search customers..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-lg text-sm text-gray-900 placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191]/30 focus:border-[#40C191] transition-colors"
            />
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="px-6 py-2.5 bg-[#40C191] text-white rounded-lg text-sm font-medium hover:bg-[#35A47C] transition-colors shrink-0"
          >
            Add Customer
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        data={customers}
        columns={columns}
        actionMenuItems={actionMenuItems}
        onAction={handleAction}
        rowsPerPage={limit}
        loading={loading}
        pagination={{ total, page, limit, pages: Math.max(1, Math.ceil(total / limit)) }}
        currentPage={page}
        onPageChange={setPage}
        showPagination={false}
      />
      <div className="flex items-center justify-center gap-2 mt-4">
        <button disabled={page===1||loading} onClick={()=>setPage(p=>Math.max(1,p-1))} className={`px-3 py-2 rounded-lg text-sm ${page===1||loading?'bg-gray-100 text-gray-400':'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Previous</button>
        <span className="text-sm text-gray-600">Page {page} of {Math.max(1, Math.ceil(total/limit))}</span>
        <button disabled={page>=Math.ceil(total/limit)||loading} onClick={()=>setPage(p=>p+1)} className={`px-3 py-2 rounded-lg text-sm ${page>=Math.ceil(total/limit)||loading?'bg-gray-100 text-gray-400':'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Next</button>
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddCustomer}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone."
        itemName={customerToDelete ? `${customerToDelete.firstName} ${customerToDelete.lastName}` : ''}
      />
    </div>
  );
};

export default Customers;

